/**
 * RhReferentielsPanel — grades, cadres d'emploi et types d'habilitation.
 *
 * ─── CE QUE CET ÉCRAN SERT À FAIRE ──────────────────────────────────────────
 *
 * Nommer une fois les valeurs qui reviennent partout. Un grade saisi à la main
 * donne « Adjoint technique principal 2e cl. », « adjoint tech. ppal 2ème
 * classe », « AT PPAL 2 »… et l'organigramme, les tris et les statistiques
 * deviennent inexploitables.
 *
 * Ce qui est défini ici alimente :
 *   · le formulaire du dossier agent,
 *   · les listes déroulantes du fichier Excel (import ET export),
 *   · l'écran des habilitations.
 *
 * ─── DÉSACTIVER, JAMAIS SUPPRIMER ───────────────────────────────────────────
 *
 * Des agents portent peut-être la valeur : la supprimer les laisserait avec un
 * grade orphelin. On la retire des listes, l'historique reste lisible. C'est
 * pourquoi il n'y a pas de bouton « supprimer » mais une bascule.
 *
 * ─── CE QU'ON NE TROUVE PAS ICI ─────────────────────────────────────────────
 *
 * Les statuts (titulaire / stagiaire / contractuel / vacataire) et les types de
 * contrat (CDI / CDD / stage / apprentissage) ne sont pas modifiables : ce sont
 * des catégories du droit de la fonction publique, pas des choix de commune.
 * Les ouvrir permettrait d'inventer un statut qui n'existe pas.
 */

import { useEffect, useState, useCallback } from 'react';
import { Card, Alert, Button, Badge } from '@/components/ui';
import { api, ApiException } from '@/lib/api';
import type {
  RhReferentiels, RhGrade, RhCadreEmploi, RhHabilitationType,
} from '@roronoa-collectivites/shared-types';
import { Plus, Info, Layers, BadgeCheck, Briefcase, Network, Route, Eye, EyeOff } from 'lucide-react';

type Onglet = 'grades' | 'cadres' | 'services' | 'ordres_mission' | 'habilitations';

export function RhReferentielsPanel() {
  const [onglet, setOnglet] = useState<Onglet>('grades');
  const [ref, setRef] = useState<RhReferentiels | null>(null);
  const [typesHab, setTypesHab] = useState<RhHabilitationType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Saisie du nouvel élément — un seul jeu de champs, réutilisé par onglet.
  const [libelle, setLibelle] = useState('');
  const [cadreId, setCadreId] = useState('');
  const [filiere, setFiliere] = useState('');
  const [dureeMois, setDureeMois] = useState('');
  const [echelonsMax, setEchelonsMax] = useState('');

  const charger = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      // `tous: true` — l'écran de gestion montre AUSSI les valeurs désactivées,
      // sinon on ne pourrait jamais les réactiver.
      const [r, t] = await Promise.all([
        api.rh.referentielsRh(true),
        api.rh.habilitations.types(),
      ]);
      setRef(r); setTypesHab(t);
    } catch (e) {
      setError(e instanceof ApiException ? e.message : 'Chargement impossible');
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { void charger(); }, [charger]);

  function reinitialiser() {
    setLibelle(''); setCadreId(''); setFiliere(''); setDureeMois(''); setEchelonsMax('');
  }

  async function ajouter() {
    if (libelle.trim().length < 2) { setError('Libellé trop court'); return; }
    setSaving(true); setError(null); setMsg(null);
    try {
      if (onglet === 'grades') {
        await api.rh.creerGrade({
          libelle: libelle.trim(),
          cadreEmploiId: cadreId || null,
          echelonsMax: echelonsMax ? Number(echelonsMax) : null,
        });
      } else if (onglet === 'cadres') {
        await api.rh.creerCadreEmploi({
          libelle: libelle.trim(),
          filiere: filiere.trim() || null,
        });
      } else if (onglet === 'services') {
        // Le libellé officiel est facultatif : sans lui, c'est le nom courant
        // qui part sur les actes.
        await api.rh.creerService({
          libelle: libelle.trim(),
          libelleOfficiel: filiere.trim() || null,
        });
      } else if (onglet === 'ordres_mission') {
        await api.rh.creerOrdreMission({ libelle: libelle.trim() });
      } else {
        await api.rh.habilitations.createType(
          libelle.trim(),
          dureeMois ? Number(dureeMois) : null,
        );
      }
      setMsg('Ajouté. La valeur est désormais proposée partout.');
      reinitialiser();
      await charger();
    } catch (e) {
      setError(e instanceof ApiException ? e.message : 'Ajout impossible');
    } finally { setSaving(false); }
  }

  async function basculer(type: 'grade' | 'cadre' | 'ordre_mission' | 'habilitation', id: string, actif: boolean) {
    try {
      if (type === 'habilitation') await api.rh.habilitations.toggleType(id, actif);
      else await api.rh.basculerReferentiel(type, id, actif);
      await charger();
    } catch (e) {
      setError(e instanceof ApiException ? e.message : 'Erreur');
    }
  }

  const champ = 'h-10 w-full rounded-r-md border border-border bg-surface/60 px-2 text-sm text-ink';
  const label = 'mb-1 block text-2xs uppercase tracking-r-eyebrow text-ink-subtle';

  const ONGLETS: Array<{ k: Onglet; label: string; icon: typeof Layers }> = [
    { k: 'grades', label: 'Grades', icon: Layers },
    { k: 'cadres', label: "Cadres d'emploi", icon: Briefcase },
    { k: 'services', label: 'Services', icon: Network },
    { k: 'ordres_mission', label: 'Ordres de mission', icon: Route },
    { k: 'habilitations', label: 'Types d\u2019habilitation', icon: BadgeCheck },
  ];

  /** Lignes affichées selon l'onglet, valeurs désactivées comprises. */
  function lignes(): Array<{ id: string; libelle: string; detail: string; actif: boolean }> {
    if (!ref) return [];
    if (onglet === 'grades') {
      return ref.grades.map((g: RhGrade) => ({
        id: g.id, libelle: g.libelle, actif: g.actif,
        detail: [g.cadreEmploiLibelle, g.echelonsMax ? `${g.echelonsMax} échelons` : null]
          .filter(Boolean).join(' · '),
      }));
    }
    if (onglet === 'cadres') {
      return ref.cadresEmploi.map((c: RhCadreEmploi) => ({
        id: c.id, libelle: c.libelle, actif: c.actif,
        detail: [c.filiere, c.categorie ? `catégorie ${c.categorie}` : null]
          .filter(Boolean).join(' · '),
      }));
    }
    if (onglet === 'services') {
      return (ref.services ?? []).map((sv) => ({
        id: sv.id, libelle: sv.libelle, actif: sv.actif,
        detail: sv.libelleOfficiel ? `sur les actes : ${sv.libelleOfficiel}` : '',
      }));
    }
    if (onglet === 'ordres_mission') {
      return (ref.ordresMission ?? []).map((o) => ({
        id: o.id, libelle: o.libelle, actif: o.actif, detail: '',
      }));
    }
    return typesHab.map((t) => ({
      id: t.id, libelle: t.libelle, actif: t.actif,
      detail: t.dureeMois ? `valide ${t.dureeMois} mois` : 'sans échéance',
    }));
  }

  const typeBascule = onglet === 'grades' ? 'grade'
    : onglet === 'cadres' ? 'cadre'
    : onglet === 'ordres_mission' ? 'ordre_mission' : 'habilitation';

  return (
    <div className="space-y-4">
      {error && <Alert variant="error">{error}</Alert>}
      {msg && <Alert variant="success">{msg}</Alert>}

      <Card variant="glass" padded>
        <h3 className="mb-3 text-sm font-semibold text-ink">Référentiels RH</h3>

        <p className="mb-4 flex items-start gap-1.5 text-xs text-ink-muted">
          <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          <span>
            Ces valeurs alimentent le formulaire des dossiers agents <strong>et les listes
            déroulantes du fichier Excel</strong>. Les définir une fois évite les
            orthographes multiples, qui rendent l'organigramme et les tris inexploitables.
            Une valeur se <strong>désactive</strong> — elle n'est jamais supprimée, car des
            agents la portent peut-être.
          </span>
        </p>

        <div className="mb-4 inline-flex rounded-md ring-1 ring-line">
          {ONGLETS.map((o, i) => (
            <button
              key={o.k} type="button"
              onClick={() => { setOnglet(o.k); reinitialiser(); setMsg(null); }}
              className={[
                'px-3 py-1.5 text-sm',
                i === 0 ? 'rounded-l-md' : '',
                i === ONGLETS.length - 1 ? 'rounded-r-md' : '',
                onglet === o.k ? 'bg-rh text-white' : 'text-ink-muted hover:text-ink',
              ].join(' ')}
            >
              {o.label}
            </button>
          ))}
        </div>

        {/* Ajout */}
        <div className="mb-4 grid gap-3 rounded-r-md border border-border bg-surface/40 p-3 sm:grid-cols-3">
          <div className={onglet === 'cadres' || onglet === 'services' || onglet === 'ordres_mission' ? 'sm:col-span-2' : ''}>
            <label className={label}>
              {onglet === 'services' ? 'Nouveau service'
                : onglet === 'grades' ? 'Nouveau grade'
                : onglet === 'cadres' ? "Nouveau cadre d'emploi"
                : onglet === 'ordres_mission' ? 'Nouvel ordre de mission'
                : 'Nouveau type d\u2019habilitation'}
            </label>
            <input value={libelle} onChange={(e) => setLibelle(e.target.value)} className={champ}
              placeholder={
                onglet === 'services' ? 'Espaces verts'
                : onglet === 'grades' ? 'Brigadier-chef principal'
                  : onglet === 'cadres' ? 'Garde champêtre'
                  : onglet === 'ordres_mission' ? 'Formation CNFPT La Rochelle'
                  : 'CACES R485 — Gerbeurs'
              } />
          </div>

          {onglet === 'grades' && (
            <>
              <div>
                <label className={label}>Cadre d'emploi</label>
                <select value={cadreId} onChange={(e) => setCadreId(e.target.value)} className={champ}>
                  <option value="">—</option>
                  {(ref?.cadresEmploi ?? []).filter((c) => c.actif).map((c) => (
                    <option key={c.id} value={c.id}>{c.libelle}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className={label}>Nombre d'échelons</label>
                <input type="number" min={1} max={20} value={echelonsMax}
                  onChange={(e) => setEchelonsMax(e.target.value)} className={champ} />
              </div>
            </>
          )}

          {onglet === 'cadres' && (
            <div>
              <label className={label}>Filière</label>
              <input value={filiere} onChange={(e) => setFiliere(e.target.value)} className={champ}
                placeholder="technique, administrative…" />
            </div>
          )}

          {onglet === 'services' && (
            <div>
              <label className={label}>Libellé officiel</label>
              <input value={filiere} onChange={(e) => setFiliere(e.target.value)} className={champ}
                placeholder="Service technique municipal" />
              <p className="mt-1 text-xs text-ink-subtle">
                Ce qui part sur les arrêtés. Vide = le nom courant est repris.
              </p>
            </div>
          )}

          {onglet === 'ordres_mission' && (
            <div className="sm:col-span-3">
              <p className="text-xs text-ink-subtle">
                Proposés en menu déroulant sur les lignes de notes de frais : la même
                mission s'écrit alors partout de la même façon.
              </p>
            </div>
          )}

          {onglet === 'habilitations' && (
            <div>
              <label className={label}>Durée de validité (mois)</label>
              <input type="number" min={1} max={240} value={dureeMois}
                onChange={(e) => setDureeMois(e.target.value)} className={champ}
                placeholder="vide = sans échéance" />
              <p className="mt-1 text-xs text-ink-subtle">
                Pré-remplit la date d'expiration à la saisie.
              </p>
            </div>
          )}

          <div className="flex items-end sm:col-span-3">
            <Button variant="primary" size="sm" loading={saving} onClick={() => void ajouter()}>
              <Plus className="h-4 w-4" aria-hidden="true" /> Ajouter
            </Button>
          </div>
        </div>

        {/* Liste */}
        {loading ? (
          <p className="text-sm text-ink-subtle">Chargement…</p>
        ) : lignes().length === 0 ? (
          <p className="text-sm text-ink-subtle">Aucune valeur.</p>
        ) : (
          <ul className="divide-y divide-border/60">
            {lignes().map((l) => (
              <li key={l.id} className="flex flex-wrap items-center gap-2 py-2">
                <span className={l.actif ? 'text-sm text-ink' : 'text-sm text-ink-subtle line-through'}>
                  {l.libelle}
                </span>
                {l.detail && <span className="text-xs text-ink-muted">{l.detail}</span>}
                {!l.actif && <Badge variant="neutral">Désactivé</Badge>}
                <Button
                  variant="ghost" size="sm" className="ml-auto"
                  onClick={() => void basculer(typeBascule, l.id, !l.actif)}
                  aria-label={`${l.actif ? 'Désactiver' : 'Réactiver'} ${l.libelle}`}
                >
                  {l.actif
                    ? <><EyeOff className="h-4 w-4" aria-hidden="true" /> Désactiver</>
                    : <><Eye className="h-4 w-4" aria-hidden="true" /> Réactiver</>}
                </Button>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Card variant="glass" padded>
        <p className="text-xs text-ink-muted">
          <strong className="text-ink">Statuts et types de contrat</strong> ne figurent pas ici :
          titulaire, stagiaire, contractuel, vacataire d'un côté ; CDI, CDD, stage,
          apprentissage de l'autre. Ce sont des catégories du droit de la fonction publique,
          non des choix de commune.
        </p>
      </Card>
    </div>
  );
}
